import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

export const EMPTY_FILTERS = { where: null, from: null, type: null, when: null };
export const hasFilters = (f) => Boolean(f.where || f.from || f.type || f.when);

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Photos' },
  { value: 'file', label: 'Files' },
  { value: 'voice', label: 'Voice' },
];
const WHEN_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

// Convert the "when" filter to ISO bounds for the API.
export const whenToRange = (when) => {
  if (!when) return {};
  const now = new Date();
  if (when.preset === 'today') return { after: startOfDay(now).toISOString() };
  if (when.preset === '7d') return { after: new Date(now.getTime() - 7 * 86400000).toISOString() };
  if (when.preset === '30d') return { after: new Date(now.getTime() - 30 * 86400000).toISOString() };
  const range = {};
  if (when.from) range.after = new Date(`${when.from}T00:00:00`).toISOString();
  if (when.to) range.before = new Date(new Date(`${when.to}T00:00:00`).getTime() + 86400000).toISOString();
  return range;
};

const whenLabel = (when) => {
  if (!when) return null;
  const preset = WHEN_PRESETS.find((p) => p.value === when.preset);
  if (preset) return preset.label;
  if (when.from && when.to) return `${shortDate(when.from)} – ${shortDate(when.to)}`;
  if (when.from) return `From ${shortDate(when.from)}`;
  if (when.to) return `Until ${shortDate(when.to)}`;
  return 'Custom';
};

// Dropdown on desktop, compact bottom sheet on phones.
const FilterMenu = ({ title, onClose, children, testId }) => (
  <>
    <div className="filter-backdrop" onClick={onClose} data-testid={`${testId}-backdrop`} />
    <div className="filter-menu" role="dialog" aria-label={title} data-testid={testId}>
      <div className="filter-menu-head">
        <span>{title}</span>
        <button type="button" className="msg-search-clear" onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}><X size={14} /></button>
      </div>
      <div className="filter-menu-body">{children}</div>
    </div>
  </>
);

const Option = ({ selected, onClick, children, testId }) => (
  <button type="button" className={`filter-option ${selected ? 'selected' : ''}`} onClick={onClick} data-testid={testId}>
    <span className="filter-option-text">{children}</span>
    {selected && <Check size={14} />}
  </button>
);

const Chip = ({ label, active, onClick, testId }) => (
  <button type="button" className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick} aria-expanded={active} data-testid={testId}>
    {label} <ChevronDown size={12} />
  </button>
);

// Where / From / Type / When chips under the message search box.
export const SearchFilters = ({ filters, onChange, conversations, userId }) => {
  const [open, setOpen] = useState(null);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const close = () => setOpen(null);
  const set = (patch) => { onChange({ ...filters, ...patch }); close(); };
  const channels = conversations.filter((c) => c.kind === 'team');
  const people = conversations.filter((c) => c.kind === 'direct');
  const whereName = conversations.find((c) => c.key === filters.where)?.name;
  const fromName = filters.from === userId ? 'You' : people.find((c) => c.id === filters.from)?.name;
  const typeName = TYPE_OPTIONS.find((t) => t.value === filters.type)?.label;

  return (
    <div className="search-filters" data-testid="search-filters">
      <div className="filter-chip-wrap">
        <Chip label={whereName ? (filters.where.startsWith('team:') ? `# ${whereName}` : whereName) : 'Where'} active={Boolean(filters.where)} onClick={() => setOpen(open === 'where' ? null : 'where')} testId="filter-where" />
        {open === 'where' && (
          <FilterMenu title="Where" onClose={close} testId="filter-where-menu">
            <Option selected={!filters.where} onClick={() => set({ where: null })} testId="filter-where-any">All conversations</Option>
            {channels.length > 0 && <div className="filter-group">Channels</div>}
            {channels.map((c) => <Option key={c.key} selected={filters.where === c.key} onClick={() => set({ where: c.key })} testId={`filter-where-${c.id}`}># {c.name}</Option>)}
            {people.length > 0 && <div className="filter-group">People</div>}
            {people.map((c) => <Option key={c.key} selected={filters.where === c.key} onClick={() => set({ where: c.key })} testId={`filter-where-${c.id}`}>{c.name}</Option>)}
          </FilterMenu>
        )}
      </div>
      <div className="filter-chip-wrap">
        <Chip label={fromName || 'From'} active={Boolean(filters.from)} onClick={() => setOpen(open === 'from' ? null : 'from')} testId="filter-from" />
        {open === 'from' && (
          <FilterMenu title="From" onClose={close} testId="filter-from-menu">
            <Option selected={!filters.from} onClick={() => set({ from: null })} testId="filter-from-any">Anyone</Option>
            <Option selected={filters.from === userId} onClick={() => set({ from: userId })} testId="filter-from-me">You</Option>
            {people.map((c) => <Option key={c.id} selected={filters.from === c.id} onClick={() => set({ from: c.id })} testId={`filter-from-${c.id}`}>{c.name}</Option>)}
          </FilterMenu>
        )}
      </div>
      <div className="filter-chip-wrap">
        <Chip label={typeName || 'Type'} active={Boolean(filters.type)} onClick={() => setOpen(open === 'type' ? null : 'type')} testId="filter-type" />
        {open === 'type' && (
          <FilterMenu title="Type" onClose={close} testId="filter-type-menu">
            <Option selected={!filters.type} onClick={() => set({ type: null })} testId="filter-type-any">Any type</Option>
            {TYPE_OPTIONS.map((t) => <Option key={t.value} selected={filters.type === t.value} onClick={() => set({ type: t.value })} testId={`filter-type-${t.value}`}>{t.label}</Option>)}
          </FilterMenu>
        )}
      </div>
      <div className="filter-chip-wrap">
        <Chip label={whenLabel(filters.when) || 'When'} active={Boolean(filters.when)} onClick={() => setOpen(open === 'when' ? null : 'when')} testId="filter-when" />
        {open === 'when' && (
          <FilterMenu title="When" onClose={close} testId="filter-when-menu">
            <Option selected={!filters.when} onClick={() => set({ when: null })} testId="filter-when-any">Any time</Option>
            {WHEN_PRESETS.map((p) => <Option key={p.value} selected={filters.when?.preset === p.value} onClick={() => set({ when: { preset: p.value } })} testId={`filter-when-${p.value}`}>{p.label}</Option>)}
            <div className="filter-group">Custom range</div>
            <div className="filter-dates">
              <label>From<input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="filter-when-from" /></label>
              <label>To<input type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="filter-when-to" /></label>
            </div>
            <button
              type="button"
              className="alerts-enable filter-apply"
              disabled={!custom.from && !custom.to}
              onClick={() => set({ when: { preset: 'custom', from: custom.from || null, to: custom.to || null } })}
              data-testid="filter-when-apply"
            >
              Apply dates
            </button>
          </FilterMenu>
        )}
      </div>
      {hasFilters(filters) && (
        <button type="button" className="filter-chip clear" onClick={() => onChange(EMPTY_FILTERS)} data-testid="filter-clear">
          <X size={12} /> Clear
        </button>
      )}
    </div>
  );
};
